import type { Options } from 'ky';

import { getProcessEnvGlobalConfig, GLOBAL_ENV_CONFIG_FIELD } from '@shared/config/global';

import Api from './ApiProvider';

export const MIXPANEL_EVENT_NAMES = {
  ROOT_DOMAIN_PAGE_VIEW: 'Root Domain Page View',
  STORE_PAGE_VIEW: 'Store Page View',
  APPS_PAGE_VIEW: 'Apps Page View',
  GAMES_PAGE_VIEW: 'Games Page View',
  UPCOMING_PAGE_VIEW: 'Upcoming Page View',
  AI_AGENTS_PAGE_VIEW: 'AI Agents Page View',
  MEMES_PAGE_VIEW: 'Memes Page View',
  TELEGRAM_MINI_APPS_PAGE_VIEW: 'Telegram MiniApps Page View',
  BANNER_CLICK: 'Banner Click',
  APPLICATION_CLICK: 'Application Click',
  APPLICATION_SEARCH: 'Application Search',
  SEE_ALL_CLICK: 'See All Click',
  CAMPAIGNS_MENU_ITEM_CLICK: 'Campaigns Click',
  CAMPAIGNS_BUTTON_CLICK: 'Campaigns Button Click',
  LAUNCHPAD_LINK_CLICK: 'Launchpad Link Click',
  LAUNCHPAD_WHITELIST_REQUIREMENTS_VIEWED: 'Launchpad Whitelist Requirements Viewed',
  STAKING_LINK_CLICK: 'Staking Link Click',
  OTC_LINK_CLICK: 'OTC Link Click',
  HOT_OFFERS_PAGE_VIEW: 'Hot Offers Page View',
  HOT_OFFER_VIEW_BUTTON_CLICK: 'Hot Offer View Button Click',
  HOT_OFFER_CLAIM_BUTTON_CLICK: 'Hot Offer Claim Button Click',
  SWAP_PAGE_VIEW: 'Swap Page View',
  SWAP_GIVE_PERMISSION_BUTTON_CLICK: 'Swap Give Permission Button Click',
  SWAP_BUTTON_CLICK: 'Swap Button Click',
  SWAP_CONFIRMATION: 'Swap Confirmation',
  VALIDATION_APP_PAGE_CLICK: 'Validation App Page Click',
  VALIDATION_USER_PROFILE_CLICK: 'Validation User Profile Click',
  FARM_POOL_STAKE_BUTTON_CLICK: 'Farm Pool Stake Click',
  FARM_POOL_BOOST_BUTTON_CLICK: 'Farm Pool Boost Click',
  FARM_POOL_CLAIM_REWARD_BUTTON_CLICK: 'Farm Pool Claim Click',
  FARM_POOL_UNSTAKE_BUTTON_CLICK: 'Farm Pool Unstake Click',
  FARM_POOLS_REGISTRY_REGISTER_CLICK: 'Farm Pools Registry Register Click',
  FARM_POOLS_REGISTRY_STAKE_CLICK: 'Farm Pools Registry Stake Click',
  CLAIM_TOKEN_CLICK: 'Claim Token Click',
  MEMBERSHIP_UPGRADE_CLICK: 'Membership Upgrade Click',
  MEMBERSHIP_DOWNGRADE_CLICK: 'Membership Downgrade Click',
  MAGIC_BOARD_CLAIM_CLICK: 'Magic Board Claim Click',
  INTERNAL_NOTIFICATIONS_CLICK: 'Internal Notifications Click',
} as const;

export type MixpanelEventName = (typeof MIXPANEL_EVENT_NAMES)[keyof typeof MIXPANEL_EVENT_NAMES];

export const MIXPANEL_PAGE = {
  HOME: 'Home',
  APPS: 'Apps',
  GAMES: 'Games',
  UPCOMING: 'Upcoming',
  HOT_OFFERS: 'Hot Offers',
  AI_AGENTS: 'AI Agents',
  MEMES: 'Memes',
  TELEGRAM_MINI_APPS: 'Telegram MiniApps',
} as const;

export type MixpanelPage = string | (typeof MIXPANEL_PAGE)[keyof typeof MIXPANEL_PAGE];

const basePath = getProcessEnvGlobalConfig(GLOBAL_ENV_CONFIG_FIELD.BASE_PATH);

export const getMixPanelPage = (): MixpanelPage => {
  let pathname = '';

  if (typeof window !== 'undefined') {
    pathname = window.location.pathname;
  } else {
    pathname = basePath + '/';
  }

  switch (true) {
    case pathname === basePath + '/':
      return MIXPANEL_PAGE.HOME;
    case pathname.startsWith(basePath + '/apps'):
      return MIXPANEL_PAGE.APPS;
    case pathname.startsWith(basePath + '/games'):
      return MIXPANEL_PAGE.GAMES;
    case pathname.startsWith(basePath + '/upcoming'):
      return MIXPANEL_PAGE.UPCOMING;
    case pathname.startsWith(basePath + '/hot-offers'):
      return MIXPANEL_PAGE.HOT_OFFERS;
    case pathname.startsWith(basePath + '/ai-agents'):
      return MIXPANEL_PAGE.AI_AGENTS;
    case pathname.startsWith(basePath + '/memes'):
      return MIXPANEL_PAGE.MEMES;
    case pathname.startsWith(basePath + '/telegram-mini-apps'):
      return MIXPANEL_PAGE.TELEGRAM_MINI_APPS;
    default:
      return `Not configured page: ${pathname}`;
  }
};

type BaseMixpanelEvent = {
  distinct_id: number | undefined;
};

export interface MixpanelEventPayload {
  [MIXPANEL_EVENT_NAMES.BANNER_CLICK]: {
    page: MixpanelPage;
    banner: string;
  };
  [MIXPANEL_EVENT_NAMES.APPLICATION_CLICK]: {
    page: MixpanelPage;
    application: string;
  };
  [MIXPANEL_EVENT_NAMES.APPLICATION_SEARCH]: {
    page: MixpanelPage;
    search: string;
  };
  [MIXPANEL_EVENT_NAMES.SEE_ALL_CLICK]: {
    page: MixpanelPage;
    group: string;
  };
  [MIXPANEL_EVENT_NAMES.LAUNCHPAD_WHITELIST_REQUIREMENTS_VIEWED]: {
    project: string;
  };
  [MIXPANEL_EVENT_NAMES.HOT_OFFER_VIEW_BUTTON_CLICK]: {
    offer: string;
  };
  [MIXPANEL_EVENT_NAMES.HOT_OFFER_CLAIM_BUTTON_CLICK]: {
    offer: string;
  };
  [MIXPANEL_EVENT_NAMES.VALIDATION_APP_PAGE_CLICK]: {
    application: string;
  };
  [MIXPANEL_EVENT_NAMES.VALIDATION_USER_PROFILE_CLICK]: {
    application: string;
  };
  [MIXPANEL_EVENT_NAMES.FARM_POOL_STAKE_BUTTON_CLICK]: {
    farm_pool_id: string;
  };
  [MIXPANEL_EVENT_NAMES.FARM_POOL_UNSTAKE_BUTTON_CLICK]: {
    farm_pool_id: string;
  };
  [MIXPANEL_EVENT_NAMES.FARM_POOL_CLAIM_REWARD_BUTTON_CLICK]: {
    farm_pool_id: string;
  };
  [MIXPANEL_EVENT_NAMES.FARM_POOLS_REGISTRY_STAKE_CLICK]: {
    farm_pool_id: string;
  };
  [MIXPANEL_EVENT_NAMES.FARM_POOLS_REGISTRY_REGISTER_CLICK]: {
    farm_pool_id: string;
  };
  [MIXPANEL_EVENT_NAMES.FARM_POOL_BOOST_BUTTON_CLICK]: {
    farm_pool_id: string;
  };
  [MIXPANEL_EVENT_NAMES.CLAIM_TOKEN_CLICK]: {
    amount: number;
    useGasStation: boolean;
    currency: string;
  };
  [MIXPANEL_EVENT_NAMES.MEMBERSHIP_UPGRADE_CLICK]: {
    currentPlan: string;
    targetPlan: string;
  };
  [MIXPANEL_EVENT_NAMES.MEMBERSHIP_DOWNGRADE_CLICK]: {
    currentPlan: string;
    targetPlan: string;
  };
  [MIXPANEL_EVENT_NAMES.MAGIC_BOARD_CLAIM_CLICK]: {
    offer: string;
  };
  [MIXPANEL_EVENT_NAMES.CAMPAIGNS_BUTTON_CLICK]: {
    campaign_name: string;
    campaign_status: string;
  };
}

export type MixpanelEvent = {
  [K in MixpanelEventName]: K extends keyof MixpanelEventPayload
    ? BaseMixpanelEvent & MixpanelEventPayload[K]
    : BaseMixpanelEvent;
};

export type NormalizedMixpanelToggles = Record<MixpanelEventName, boolean>;

interface MixpanelToggle {
  event_name: MixpanelEventName;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export const getMixpanelToggles = async (options?: Options) => {
  return Api.get('v2/trace/business-events/settings', {
    ...options,
  }).json<MixpanelToggle[]>();
};

export const normalizeMixpanelToggles = (data: MixpanelToggle[]): NormalizedMixpanelToggles => {
  return data?.reduce<NormalizedMixpanelToggles>((acc, row) => {
    return {
      ...acc,
      [row.event_name]: row.enabled,
    };
  }, {} as NormalizedMixpanelToggles);
};

export const getNormalizedMixpanelToggles = async (options?: Options) => {
  const data = await getMixpanelToggles(options);

  return normalizeMixpanelToggles(data);
};
